// src/components/ChatContext.js
import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { auth, getIdToken } from '../firebase';
import axios from 'axios';

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }) => {
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [unreadCounts, setUnreadCounts] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const [usePolling, setUsePolling] = useState(false); // Fallback to HTTP polling
  
  const ws = useRef(null);
  const messageHandlers = useRef(new Set());
  const reconnectTimeout = useRef(null);
  const pollInterval = useRef(null);

  // Wrap handlers in useCallback to prevent infinite loops
  const handleNewMessage = useCallback((message) => {
    setMessages(prev => {
      if (prev.some(m => m.id === message.id)) return prev;
      return [...prev, message];
    });

    setChats(prev => {
      const existingChat = prev.find(c => c.id === message.chatId);
      if (existingChat) {
        return prev.map(c => 
          c.id === message.chatId 
            ? { ...c, lastMessage: message, updatedAt: new Date() }
            : c
        );
      }
      return prev;
    });

    if (activeChat?.id !== message.chatId) {
      setUnreadCounts(prev => ({
        ...prev,
        [message.chatId]: (prev[message.chatId] || 0) + 1
      }));
    }

    messageHandlers.current.forEach(handler => handler(message));
  }, [activeChat?.id]);

  const handleMessageRead = useCallback((chatId, messageId) => {
    setMessages(prev => 
      prev.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      )
    );
  }, []);

  const handleTyping = useCallback((chatId, userId) => {
    // Implement typing indicator logic if needed
    console.log('User typing:', userId, 'in chat:', chatId);
  }, []);

  // ===== DEFINE markAsRead FIRST =====
  const markAsRead = useCallback(async (chatId) => {
    const user = auth.currentUser;
    if (!user) return;

    // Update local state
    setUnreadCounts(prev => ({
      ...prev,
      [chatId]: 0
    }));

    // Try WebSocket if connected
    if (wsConnected && ws.current) {
      try {
        ws.current.send(JSON.stringify({
          type: 'MARK_READ',
          chatId
        }));
      } catch (error) {
        console.error('Error marking as read via WebSocket:', error);
      }
    }

    // Always call HTTP endpoint to persist
    try {
      const token = await getIdToken();
      await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/chats/${chatId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error marking as read via HTTP:', error);
    }
  }, [wsConnected]);

  // HTTP Polling fallback for when WebSocket fails
  const startPolling = useCallback(() => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
    }

    console.log('Starting HTTP polling fallback...');
    
    pollInterval.current = setInterval(async () => {
      try {
        const user = auth.currentUser;
        if (!user || !activeChat?.id) return;

        const token = await getIdToken();
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/chats/${activeChat.id}/messages?since=${Date.now() - 30000}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data && res.data.length > 0) {
          res.data.forEach(message => {
            handleNewMessage(message);
          });
        }
      } catch (error) {
        console.error('Error polling for messages:', error);
      }
    }, 3000); // Poll every 3 seconds
  }, [activeChat?.id, handleNewMessage]);

  // Connect WebSocket function
  const connectWebSocket = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      console.log('No user logged in, skipping WebSocket connection');
      return;
    }

    // Clear any existing reconnect timeout
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }

    // If we're already in polling mode, don't try WebSocket
    if (usePolling) {
      startPolling();
      return;
    }

    try {
      const token = await getIdToken();
      
      // IMPORTANT: Use the chat-specific WebSocket URL
      const wsUrl = process.env.REACT_APP_CHAT_WS_URL || 'wss://www.teachmego.com/ws/chat';
      
      // Append token as query parameter
      const fullUrl = `${wsUrl}?token=${token}`;
      
      console.log('Connecting to chat WebSocket:', fullUrl);
      
      ws.current = new WebSocket(fullUrl);

      // Set a timeout to detect if WebSocket fails to connect
      const connectionTimeout = setTimeout(() => {
        if (ws.current && ws.current.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection timeout, falling back to HTTP polling');
          ws.current.close();
          setUsePolling(true);
          setWsConnected(false);
          startPolling();
        }
      }, 5000);

      ws.current.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('Chat WebSocket connected successfully');
        setWsConnected(true);
        setUsePolling(false);
        
        // Stop polling if it was running
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
        }
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'NEW_MESSAGE':
              handleNewMessage(data.message);
              break;
            case 'MESSAGES_READ':
              if (data.messageIds) {
                data.messageIds.forEach(messageId => {
                  handleMessageRead(data.chatId, messageId);
                });
              }
              break;
            case 'USER_ONLINE':
              setOnlineUsers(prev => new Set([...prev, data.userId]));
              break;
            case 'USER_OFFLINE':
              setOnlineUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(data.userId);
                return newSet;
              });
              break;
            case 'USER_JOINED':
              console.log('User joined:', data.username);
              break;
            case 'USER_LEFT':
              console.log('User left:', data.username);
              break;
            case 'TYPING':
              handleTyping(data.chatId, data.userId);
              break;
            default:
              console.log('Unknown message type:', data.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.current.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('Chat WebSocket error:', error);
        setWsConnected(false);
        
        // Fall back to HTTP polling
        console.log('WebSocket error, falling back to HTTP polling');
        setUsePolling(true);
        startPolling();
      };

      ws.current.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('Chat WebSocket disconnected:', event.code, event.reason);
        setWsConnected(false);
        
        // Don't reconnect if we're already using polling
        if (!usePolling && event.code !== 1000) {
          console.log('Attempting to reconnect in 5 seconds...');
          reconnectTimeout.current = setTimeout(connectWebSocket, 5000);
        }
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      setWsConnected(false);
      setUsePolling(true);
      startPolling();
    }
  }, [handleNewMessage, handleMessageRead, handleTyping, startPolling, usePolling]);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    connectWebSocket();

    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
      if (ws.current) {
        ws.current.close(1000, 'Component unmounting');
      }
    };
  }, [connectWebSocket]);

  const fetchChats = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;

    setLoading(true);
    try {
      const token = await getIdToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/user/${user.uid}/chats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChats(res.data);
      
      const counts = {};
      res.data.forEach(chat => {
        counts[chat.id] = chat.unreadCount?.[user.uid] || 0;
      });
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // FIXED: Now markAsRead is defined above, so this works
  const fetchMessages = useCallback(async (chatId) => {
    const user = auth.currentUser;
    if (!user) return;

    setLoading(true);
    try {
      const token = await getIdToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/chats/${chatId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(res.data);
      
      // Mark as read when fetching messages
      await markAsRead(chatId);
      
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [markAsRead]); // Now markAsRead is defined before this

  const sendMessage = async (chatId, content, type = 'text') => {
    const user = auth.currentUser;
    if (!user) {
      console.error('No user logged in');
      return null;
    }

    const tempId = Date.now().toString();
    const messageData = {
      id: tempId,
      chatId,
      content,
      type,
      senderId: user.uid,
      createdAt: new Date().toISOString(),
      tempId,
      read: false
    };

    // Optimistically add message to UI
    handleNewMessage(messageData);

    // Try WebSocket first if connected
    if (wsConnected && ws.current) {
      try {
        ws.current.send(JSON.stringify({
          type: 'SEND_MESSAGE',
          message: messageData
        }));
        return messageData;
      } catch (error) {
        console.error('Error sending message via WebSocket:', error);
      }
    }

    // Fallback to HTTP
    try {
      const token = await getIdToken();
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/chats/${chatId}/messages`,
        { content, type, tempId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Replace temp message with real one
      setMessages(prev => 
        prev.map(m => m.tempId === tempId ? res.data : m)
      );
      
      return res.data;
    } catch (error) {
      console.error('Error sending message via HTTP:', error);
      // Remove optimistically added message on failure
      setMessages(prev => prev.filter(m => m.tempId !== tempId));
      return null;
    }
  };

  const createChat = async (userId) => {
    const user = auth.currentUser;
    if (!user) return null;

    try {
      const token = await getIdToken();
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/chats`,
        { participantId: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setChats(prev => [res.data, ...prev]);
      
      return res.data;
    } catch (error) {
      console.error('Error creating chat:', error);
      return null;
    }
  };

  const sendTyping = (chatId, isTyping) => {
    if (!wsConnected || !ws.current) return;

    try {
      ws.current.send(JSON.stringify({
        type: 'TYPING',
        chatId,
        isTyping
      }));
    } catch (error) {
      console.error('Error sending typing indicator:', error);
    }
  };

  const addMessageHandler = (handler) => {
    messageHandlers.current.add(handler);
    return () => messageHandlers.current.delete(handler);
  };

  const value = {
    chats,
    activeChat,
    setActiveChat,
    messages,
    loading,
    onlineUsers,
    unreadCounts,
    wsConnected,
    usePolling,
    fetchChats,
    fetchMessages,
    sendMessage,
    createChat,
    markAsRead,
    sendTyping,
    addMessageHandler
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
